import React, { useState, useEffect, useRef, useCallback, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import { MotionConfig, motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Accessibility, X, RotateCcw, Contrast, SunMoon, Droplet, Link2, MousePointer2,
  Type, AlignJustify, Minus, PauseCircle, ImageOff, Volume2, Square, Plus,
} from 'lucide-react';
import './AccessibilityMenu.css';

const STORAGE_KEY = 'ff-a11y-settings';
const TEXT_SCALES = [1, 1.1, 1.25, 1.4, 1.5];

const DEFAULTS = {
  textScale: 0,          // TEXT_SCALES indeksi
  contrast: 'none',      // none | high | invert | gray
  highlightLinks: false,
  bigCursor: false,
  readableFont: false,
  spacing: false,
  readingGuide: false,
  stopAnimations: false,
  hideImages: false,
  tts: false,
};

const LABELS = {
  tr: {
    open: 'Erişilebilirlik menüsü', title: 'Erişilebilirlik', close: 'Kapat', reset: 'Tüm ayarları sıfırla',
    textSize: 'Yazı boyutu', smaller: 'Küçült', larger: 'Büyüt',
    sView: 'Görünüm', sRead: 'Okuma', sMotion: 'Hareket ve medya', sAudio: 'Sesli okuma',
    high: 'Yüksek kontrast', invert: 'Renkleri ters çevir', gray: 'Gri tonlama',
    links: 'Bağlantıları vurgula', cursor: 'Büyük imleç',
    font: 'Okunaklı yazı tipi', spacing: 'Satır ve harf aralığı', guide: 'Okuma çizgisi',
    anim: 'Animasyonları durdur', images: 'Görselleri gizle',
    tts: 'Tıklayınca oku',
    readPage: 'Sayfayı oku', stop: 'Durdur',
    note: 'Ayarlarınız bu tarayıcıda saklanır.',
  },
  en: {
    open: 'Accessibility menu', title: 'Accessibility', close: 'Close', reset: 'Reset all settings',
    textSize: 'Text size', smaller: 'Smaller', larger: 'Larger',
    sView: 'Display', sRead: 'Reading', sMotion: 'Motion & media', sAudio: 'Text to speech',
    high: 'High contrast', invert: 'Invert colors', gray: 'Grayscale',
    links: 'Highlight links', cursor: 'Large cursor',
    font: 'Readable font', spacing: 'Line & letter spacing', guide: 'Reading guide',
    anim: 'Stop animations', images: 'Hide images',
    tts: 'Read on click',
    readPage: 'Read page', stop: 'Stop',
    note: 'Your settings are saved in this browser.',
  },
};

function loadSettings() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    const out = { ...DEFAULTS };
    Object.keys(DEFAULTS).forEach(k => { if (k in saved) out[k] = saved[k]; });
    return out;
  }
  catch { return { ...DEFAULTS }; }
}

const A11yContext = createContext({ settings: DEFAULTS });
export const useA11y = () => useContext(A11yContext);

// ─── Sesli okuma ───────────────────────────────────────────────────────────
const canSpeak = typeof window !== 'undefined' && 'speechSynthesis' in window;
if (canSpeak) window.speechSynthesis.getVoices(); // Chrome sesleri ilk çağrıda yüklemeye başlar

// Cihazdaki sesleri kaliteye göre puanla: doğal/nöral bulut sesleri en üstte
function voiceScore(v) {
  let s = 0;
  if (/natural|neural|wavenet|premium|enhanced/i.test(v.name)) s += 100;
  if (/online/i.test(v.name)) s += 40;
  if (/google/i.test(v.name)) s += 60;
  if (/emel|ahmet|yelda|jenny|aria|guy|samantha/i.test(v.name)) s += 15;
  if (!v.localService) s += 10;
  return s;
}

function getVoices(lang) {
  if (!canSpeak) return [];
  const prefix = lang === 'en' ? 'en' : 'tr';
  return window.speechSynthesis.getVoices()
    .filter(v => v.lang?.toLowerCase().replace('_', '-').startsWith(prefix))
    .sort((a, b) => voiceScore(b) - voiceScore(a));
}

// Cihazdaki en kaliteli sesi otomatik seç
function pickVoice(lang) {
  return getVoices(lang)[0] || null;
}

// Uzun metni cümlelere böl: daha doğal duraklamalar sağlar ve Chrome'un
// ~15 saniyede konuşmayı kesme hatasını önler
function chunkText(text, max = 220) {
  const sentences = text.match(/[^.!?…\n]+[.!?…]*\s*/g) || [text];
  const chunks = [];
  let cur = '';
  const push = () => { if (cur.trim()) chunks.push(cur.trim()); cur = ''; };
  sentences.forEach(s => {
    if ((cur + s).length <= max) { cur += s; return; }
    push();
    if (s.length <= max) { cur = s; return; }
    // Çok uzun cümle: virgül/boşluklardan böl
    s.split(/(?<=[,;:])\s+|\s+/).forEach(part => {
      if ((cur + ' ' + part).length > max) push();
      cur += (cur ? ' ' : '') + part;
    });
  });
  push();
  return chunks;
}

function speak(text, lang) {
  if (!canSpeak || !text) return;
  window.speechSynthesis.cancel();
  const clean = text.replace(/\s+/g, ' ').trim().slice(0, 30000);
  if (!clean) return;
  const voice = pickVoice(lang);
  chunkText(clean).forEach(chunk => {
    const u = new SpeechSynthesisUtterance(chunk);
    u.lang = voice?.lang || (lang === 'en' ? 'en-US' : 'tr-TR');
    if (voice) u.voice = voice;
    u.rate = 1;
    u.pitch = 1;
    window.speechSynthesis.speak(u);
  });
}
function stopSpeaking() { if (canSpeak) window.speechSynthesis.cancel(); }

// ─── Provider: ayarları <html>'e uygular, framer-motion'ı yönetir ───────────
export function AccessibilityProvider({ children }) {
  const [settings, setSettings] = useState(loadSettings);
  const { i18n } = useTranslation();
  const lang = i18n.language === 'en' ? 'en' : 'tr';

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(settings)); } catch { /* depolama kapalı olabilir */ }

    const root = document.documentElement;
    const toggle = (cls, on) => root.classList.toggle(cls, !!on);
    toggle('a11y-contrast-high', settings.contrast === 'high');
    toggle('a11y-invert',        settings.contrast === 'invert');
    toggle('a11y-gray',          settings.contrast === 'gray');
    toggle('a11y-links',         settings.highlightLinks);
    toggle('a11y-cursor',        settings.bigCursor);
    toggle('a11y-font',          settings.readableFont);
    toggle('a11y-spacing',       settings.spacing);
    toggle('a11y-no-motion',     settings.stopAnimations);
    toggle('a11y-hide-images',   settings.hideImages);
    root.style.setProperty('--a11y-zoom', TEXT_SCALES[settings.textScale] || 1);

    // Okunaklı yazı tipini sadece gerektiğinde yükle
    if (settings.readableFont && !document.getElementById('a11y-font-link')) {
      const link = document.createElement('link');
      link.id = 'a11y-font-link';
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Atkinson+Hyperlegible:ital,wght@0,400;0,700;1,400&display=swap';
      document.head.appendChild(link);
    }

    // Animasyonlar durdurulunca oynayan videoları da durdur
    if (settings.stopAnimations) document.querySelectorAll('video').forEach(v => { try { v.pause(); } catch { /* yok say */ } });
  }, [settings]);

  // Tıklayınca / seçince sesli oku
  useEffect(() => {
    if (!settings.tts) return;
    const onUp = (e) => {
      if (e.target.closest?.('.a11y-panel, .a11y-fab')) return;
      const sel = window.getSelection()?.toString();
      if (sel && sel.trim().length > 1) { speak(sel, lang); return; }
      const el = e.target.closest?.('p, h1, h2, h3, h4, h5, h6, li, a, button, label, td, th, figcaption, blockquote, span, div');
      if (!el) return;
      speak(el.getAttribute('aria-label') || el.innerText || el.getAttribute('alt'), lang);
    };
    document.addEventListener('mouseup', onUp);
    return () => { document.removeEventListener('mouseup', onUp); stopSpeaking(); };
  }, [settings.tts, lang]);

  return (
    <A11yContext.Provider value={{ settings, setSettings, lang }}>
      <MotionConfig reducedMotion={settings.stopAnimations ? 'always' : 'user'}>
        {children}
        <AccessibilityMenu />
        {settings.readingGuide && <ReadingGuide />}
      </MotionConfig>
    </A11yContext.Provider>
  );
}

// ─── Okuma çizgisi ──────────────────────────────────────────────────────────
function ReadingGuide() {
  const [y, setY] = useState(-100);
  useEffect(() => {
    const onMove = (e) => setY(e.clientY);
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);
  return createPortal(
    <div aria-hidden="true" className="a11y-reading-guide">
      <div style={{ height: Math.max(0, y - 22) }} className="a11y-guide-shade" />
      <div style={{ height: 44 }} className="a11y-guide-window" />
      <div style={{ flex: 1 }} className="a11y-guide-shade" />
    </div>,
    document.body
  );
}

// ─── Menü ───────────────────────────────────────────────────────────────────
function Toggle({ icon: Icon, label, on, onClick }) {
  return (
    <button type="button" className={`a11y-tile${on ? ' is-on' : ''}`} aria-pressed={on} onClick={onClick}>
      <span className="a11y-tile-icon"><Icon size={20} /></span>
      <span className="a11y-tile-label">{label}</span>
    </button>
  );
}

function AccessibilityMenu() {
  const { settings, setSettings, lang } = useA11y();
  const [open, setOpen] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const panelRef = useRef(null);
  const fabRef = useRef(null);
  const L = LABELS[lang];

  const set = useCallback((patch) => setSettings(s => ({ ...s, ...patch })), [setSettings]);
  const flip = (key) => set({ [key]: !settings[key] });
  const setContrast = (mode) => set({ contrast: settings.contrast === mode ? 'none' : mode });

  const close = useCallback(() => { setOpen(false); fabRef.current?.focus(); }, []);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => panelRef.current?.querySelector('button')?.focus(), 60);
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    const onDown = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) && !fabRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => { clearTimeout(t); document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown); };
  }, [open, close]);

  // Konuşma durumunu izle (Durdur düğmesi için)
  useEffect(() => {
    if (!canSpeak) return;
    const id = setInterval(() => setSpeaking(window.speechSynthesis.speaking), 400);
    return () => clearInterval(id);
  }, []);

  const readPage = () => {
    const main = document.getElementById('main-content');
    speak(main?.innerText || document.body.innerText, lang);
  };

  const changed = JSON.stringify(settings) !== JSON.stringify(DEFAULTS);
  const pct = Math.round((TEXT_SCALES[settings.textScale] || 1) * 100);

  return createPortal(
    <>
      <button
        ref={fabRef}
        type="button"
        className="a11y-fab"
        aria-label={L.open}
        title={L.open}
        aria-expanded={open}
        aria-controls="a11y-panel"
        onClick={() => setOpen(o => !o)}
      >
        <Accessibility size={26} />
        {changed && <span className="a11y-fab-dot" aria-hidden="true" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            ref={panelRef}
            id="a11y-panel"
            className="a11y-panel"
            role="dialog"
            aria-modal="false"
            aria-labelledby="a11y-title"
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -16 }}
            transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <div className="a11y-head">
              <span className="a11y-head-icon"><Accessibility size={20} /></span>
              <h2 id="a11y-title">{L.title}</h2>
              <button type="button" className="a11y-icon-btn" onClick={close} aria-label={L.close} title={L.close}>
                <X size={18} />
              </button>
            </div>

            <div className="a11y-body">
              {/* Yazı boyutu */}
              <div className="a11y-section-title">{L.textSize}</div>
              <div className="a11y-scale">
                <button type="button" className="a11y-scale-btn" aria-label={L.smaller}
                  disabled={settings.textScale === 0}
                  onClick={() => set({ textScale: Math.max(0, settings.textScale - 1) })}>
                  <Minus size={18} />
                </button>
                <div className="a11y-scale-mid">
                  <span className="a11y-scale-value" aria-live="polite">%{pct}</span>
                  <span className="a11y-scale-dots" aria-hidden="true">
                    {TEXT_SCALES.map((_, i) => <i key={i} className={i <= settings.textScale ? 'on' : ''} />)}
                  </span>
                </div>
                <button type="button" className="a11y-scale-btn" aria-label={L.larger}
                  disabled={settings.textScale === TEXT_SCALES.length - 1}
                  onClick={() => set({ textScale: Math.min(TEXT_SCALES.length - 1, settings.textScale + 1) })}>
                  <Plus size={18} />
                </button>
              </div>

              <div className="a11y-section-title">{L.sView}</div>
              <div className="a11y-grid">
                <Toggle icon={Contrast} label={L.high}   on={settings.contrast === 'high'}   onClick={() => setContrast('high')} />
                <Toggle icon={SunMoon}  label={L.invert} on={settings.contrast === 'invert'} onClick={() => setContrast('invert')} />
                <Toggle icon={Droplet}  label={L.gray}   on={settings.contrast === 'gray'}   onClick={() => setContrast('gray')} />
                <Toggle icon={Link2}    label={L.links}  on={settings.highlightLinks} onClick={() => flip('highlightLinks')} />
                <Toggle icon={MousePointer2} label={L.cursor} on={settings.bigCursor} onClick={() => flip('bigCursor')} />
              </div>

              <div className="a11y-section-title">{L.sRead}</div>
              <div className="a11y-grid">
                <Toggle icon={Type}         label={L.font}    on={settings.readableFont} onClick={() => flip('readableFont')} />
                <Toggle icon={AlignJustify} label={L.spacing} on={settings.spacing}      onClick={() => flip('spacing')} />
                <Toggle icon={Minus}        label={L.guide}   on={settings.readingGuide} onClick={() => flip('readingGuide')} />
              </div>

              <div className="a11y-section-title">{L.sMotion}</div>
              <div className="a11y-grid">
                <Toggle icon={PauseCircle} label={L.anim}   on={settings.stopAnimations} onClick={() => flip('stopAnimations')} />
                <Toggle icon={ImageOff}    label={L.images} on={settings.hideImages}     onClick={() => flip('hideImages')} />
              </div>

              {canSpeak && (
                <>
                  <div className="a11y-section-title">{L.sAudio}</div>
                  <div className="a11y-grid">
                    <Toggle icon={Volume2} label={L.tts} on={settings.tts} onClick={() => { if (settings.tts) stopSpeaking(); flip('tts'); }} />
                    {speaking ? (
                      <button type="button" className="a11y-tile is-stop" onClick={stopSpeaking}>
                        <span className="a11y-tile-icon"><Square size={18} /></span>
                        <span className="a11y-tile-label">{L.stop}</span>
                      </button>
                    ) : (
                      <button type="button" className="a11y-tile" onClick={readPage}>
                        <span className="a11y-tile-icon"><Volume2 size={20} /></span>
                        <span className="a11y-tile-label">{L.readPage}</span>
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="a11y-foot">
              <button type="button" className="a11y-reset" disabled={!changed}
                onClick={() => { stopSpeaking(); setSettings({ ...DEFAULTS }); }}>
                <RotateCcw size={15} /> {L.reset}
              </button>
              <span className="a11y-note">{L.note}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>,
    document.body
  );
}
